/**
 * The SEQUENCE canvas as one mountable unit: the column row and the move plumbing behind it.
 *
 * The twin of `WeekCanvas`, and it exists for the same structural reason: each canvas owns a
 * Pragmatic monitor, and every monitor whose `canMonitor` accepts a source sees that source's drop —
 * so two mounted at once would dispatch one gesture twice. The screen mounts exactly one of these,
 * which is what makes "one move mechanic is live at a time" a property rather than a rule.
 *
 * The card's DAY ACTIONS live here for the same reason — see `WeekCanvas`, whose note applies word
 * for word: one canvas mounted means one live region and one delete confirm.
 *
 * ── And so does REVEALING the day the panel opened on ────────────────────────────────────────
 * The 574px panel overlays this canvas's right-hand edge and this canvas is the one that scrolls
 * sideways, so `useRevealOpenDay` belongs here on the same argument that puts the move here: the
 * pane below is the scroller and must not learn that a panel exists, and the screen above mounts one
 * of two canvases and would have to ask which. `TapePane` stamps `data-canvas-scroller` on its
 * scrollport and `data-day-slot` on every training column; the hook finds both by attribute. Without
 * this the sequence canvas stamped the columns and nothing ever measured them — the attribute's own
 * comment claimed a reader that was mounted only in `WeekCanvas`.
 */

import { useScreenMotionPreference } from '~/shared/design-system/motion';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';

import { useDayActions } from '../lib/useDayActions';
import { useRevealOpenDay } from '../lib/useRevealOpenDay';
import { useTapeMove } from '../lib/useTapeMove';
import { useMovedCardFocus } from '../lib/useMovedCardFocus';
import type { DayDockMode } from '../lib/usePlanBuilderSearch';
import { overlayRoom } from '../model/revealDay';
import type { TapeViewModel } from '../model/tapeViewModel';

import { TAPE_MOVE_HINT_ID, TapePane } from './TapePane';

interface TapeCanvasProps {
  readonly tape: TapeViewModel;
  readonly panelId: string;
  readonly labelledBy: string;
  readonly onRenameSession: ((slotToken: string, name: string) => void) | null;
  readonly onProgramDay: ((slotToken: string) => void) | null;
  readonly onChangeRest: ((daySlotToken: string, days: number) => void) | null;
  readonly onAddDay: (() => void) | null;
  readonly returnFocusToken: string | null;
  /** False on a plan that cannot be edited — no grip, no ⋯, no drop targets, no stepper. */
  readonly editable: boolean;
  /** The phase in view, so the pane can replay the phase-switch fade. See `TapePaneProps`. */
  readonly phaseIndex: number;
  /**
   * Which day the dock is open on and in which host, or `null` when it is closed.
   *
   * Two jobs: the pane paints that column's selection ring, and this canvas scrolls the column clear
   * of the panel. The same prop and the same shape as `WeekCanvas`'s, deliberately — see its header
   * for why the pair lives on the canvas rather than on the pane or on the screen.
   */
  readonly openDay: { readonly slotToken: string; readonly dock: DayDockMode } | null;
  /** The Buddy panel is open over the canvas's right-hand end (owner, 7 October 2026). */
  readonly assistantOpen: boolean;
}

export function TapeCanvas({
  tape,
  panelId,
  labelledBy,
  onRenameSession,
  onProgramDay,
  onChangeRest,
  onAddDay,
  returnFocusToken,
  editable,
  phaseIndex,
  openDay,
  assistantOpen,
}: TapeCanvasProps) {
  // How many stops the cycle has — the "z 5" of every announcement. TRAININGS only: a gap column is
  // not a position a day can be sent to, so counting the row's columns would give the keyboard path
  // twice as many stops as there are destinations.
  const total = tape.columns.filter((column) => column.kind === 'day').length;
  const move = useTapeMove(total);
  /* The dock's returning card and the one a move just carried to another column, merged — see
   * `useMovedCardFocus` for why a committed move owes focus at all. */
  const focusToken = useMovedCardFocus(returnFocusToken, move);
  // See `WeekCanvas`: the ⋯ that opened the dialog is gone by the time focus would return to it.
  const actions = useDayActions(move.announce, () => {
    document.getElementById(panelId)?.focus();
  });

  // The panel overlays the canvas's right-hand 574px, so a day opened under it would be invisible
  // with its selection ring. The preference is read here and passed IN — a scripted scroll cannot be
  // reached by the one `prefers-reduced-motion` block in `tokens/base.css`.
  const reducedMotion = useScreenMotionPreference();
  useRevealOpenDay({
    daySlotToken: openDay?.slotToken ?? null,
    dock: openDay?.dock ?? null,
    reducedMotion,
  });

  return (
    <>
      <TapePane
        id={panelId}
        tape={tape}
        labelledBy={labelledBy}
        onRenameSession={onRenameSession}
        onProgramDay={onProgramDay}
        onChangeRest={onChangeRest}
        onAddDay={onAddDay}
        returnFocusToken={focusToken}
        phaseIndex={phaseIndex}
        openDaySlotToken={openDay?.slotToken ?? null}
        panelRoom={overlayRoom(openDay?.dock === 'panel', assistantOpen)}
        actions={editable ? { view: tape.chrome.dayActions, ...actions } : null}
        announcement={move.announcement}
        pendingTarget={move.pendingTarget}
        move={
          editable
            ? {
                hintId: TAPE_MOVE_HINT_ID,
                sourceLabel: tape.chrome.moveSourceLabel,
                cancelledMessage: tape.chrome.moveCancelled,
                moveDay: move.moveDay,
                announce: move.announce,
                setPendingTarget: move.setPendingTarget,
              }
            : null
        }
      />
      {/* One canvas is mounted at a time, so this is the ONE delete confirm on screen. */}
      <ConfirmDialog
        prompt={actions.prompt}
        onConfirm={actions.confirmDelete}
        onCancel={actions.cancelDelete}
      />
    </>
  );
}
